import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { isEmptyArray } from '@common/helpers';
import { ServiceContentsI18n } from '@common/interfaces/master/service_contents_by_itinerary_type_cabin';
import { MasterDataS01P031 } from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import { ServicingCommonStoreService } from '@common/services';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { TripSummaryService } from '@common/services/my-booking/trip-summary.service';
import { SupportComponent } from '@lib/components/support-class';
import { MetaUrlPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription } from 'rxjs';
import { GetOrderResponseData, Type1 } from 'src/sdk-servicing';
import { AppConstants } from '@conf/app.constants';

@Component({
  selector: 'asw-in-flight-service',
  templateUrl: './in-flight-service.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InFlightServiceComponent extends SupportComponent {
  id = this.constructor.name;
  @Input() boundIdx!: number;
  @Input() flightIdx!: number;

  flight: Type1 = {};
  foodAndDrink: { foodDrinkIcon?: string; foodDrinkAlt?: string } = {};
  foodAndDrinkTooltipContent: string = '';
  entertainmentTooltipContent: string = '';
  shoppingTooltipContent: string = '';
  amenitiesTooltipContent: string = '';
  loungeTooltipContent: string = '';
  seatTooltipContent: string = '';
  wifiTooltipContent: { wifiIcon?: string; wifiAlt?: string } = {
    wifiIcon: '',
    wifiAlt: '',
  };

  private _subscriptions = new Subscription();
  private _serviceContentsMaster?: Array<ServiceContentsI18n>;
  private _wiFiType = '';

  // 定数用
  public appConstants = AppConstants;

  constructor(
    public tripSummarySvc: TripSummaryService,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _staticMsg: StaticMsgPipe,
    private _metaUrlPipe: MetaUrlPipe,
    private _change: ChangeDetectorRef,
    private _common: CommonLibService
  ) {
    super(_common);
  }

  override init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        const pnr = (servicingCommon.getOrder as GetOrderResponseData) ?? {};
        this.flight = pnr.air?.bounds?.at(this.boundIdx)?.flights?.at(this.flightIdx) ?? {};
        this._wiFiType = pnr.air?.bounds?.at(this.boundIdx)?.wiFiType ?? '';
        // this.flight.
        this.foodAndDrink = this.buildFoodAndDrink();
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._myBookingItineraryService.masterData$.subscribe((result: MasterDataS01P031) => {
        this._serviceContentsMaster = result.serviceContents ?? [];
        if (!isEmptyArray(this._serviceContentsMaster)) {
          // [1.1.35] 食事・飲み物
          this.foodAndDrinkTooltipContent = this.buildTooltipContent('3', 'alt.foodAndDirnk', this.flight);
          // [1.1.36] エンターテイメント
          this.entertainmentTooltipContent = this.buildTooltipContent('4', 'alt.entertainment', this.flight);
          // [1.1.37] ショッピング
          this.shoppingTooltipContent = this.buildTooltipContent('5', 'alt.shopping', this.flight);
          // [1.1.38] アメニティ
          this.amenitiesTooltipContent = this.buildTooltipContent('6', 'alt.amenities', this.flight);
          // [1.1.39] ラウンジ
          this.loungeTooltipContent = this.buildTooltipContent('1', 'alt.lounge', this.flight);
          // [1.1.40] 座席
          this.seatTooltipContent = this.buildTooltipContent('2', 'alt.seat', this.flight);
        }

        this.wifiTooltipContent = this.buildWifiToolTip();
        this._change.markForCheck();
      })
    );
  }

  // (1.1.35) 食事・飲み物
  buildFoodAndDrink(): { foodDrinkIcon: string; foodDrinkAlt: string } {
    // 以下の条件に応じて表示する。
    switch (this.flight.meal?.mealCode) {
      // 1.1) 当該セグメント情報.meal.mealCode="S"(茶菓)の場合、
      case 'S':
        // 茶菓のアイコン
        return {
          foodDrinkIcon: 'icon_cakes_primary_24.svg',
          foodDrinkAlt: 'alt.tea',
        };
      // 1.2) 当該セグメント情報.meal.mealCode="N"(機内食の提供がない)の場合、食事なしのアイコン
      case 'N':
        // 軽食のアイコン
        return {
          foodDrinkIcon: 'icon_drink_primary_24.svg',
          foodDrinkAlt: 'alt.noMeal',
        };
      // 1.3)  上記以外の場合、 食事のアイコン
      default:
        return {
          foodDrinkIcon: 'icon_meal_primary_28.svg',
          foodDrinkAlt: 'alt.meal',
        };
    }
  }
  // (1.1.36) エンターテイメント
  entertainmentIcon = 'icon_entertainment_primary_28.svg';
  // (1.1.37) ショッピング
  shoppingIcon = 'icon_shopping_primary_28.svg';
  // (1.1.38) アメニティ
  amenitiesIcon = 'icon_amenity_primary_28.svg';
  // (1.1.39) ラウンジ
  loungeIcon = 'icon_lounge_primary_28.svg';
  // (1.1.40) 座席
  seatIcon = 'icon_seat_primary_28.svg';

  // (1.1.62) Wi-Fiサービス有無
  buildWifiToolTip(): { wifiIcon?: string; wifiAlt?: string } {
    /** 1)
     * 当該セグメント情報.aircraftConfigurationVersionを基に「イベントID(1)」で取得した機材リストをフィルタして、Wi-Fi利用区分を取得す
     * る。以下の条件に従いWi-Fi利用区分に応じたイメージを表示する。
     */
    const wifiFlag = this._wiFiType;
    // 2) 以下の条件に応じて表示する。
    switch (wifiFlag) {
      // 2.1) Wi-Fi利用区分="0"(なし)の場合、Wi-Fi無を表すイメージ
      case '0':
        return {
          wifiIcon: this.appConstants.IMG.S01_P030_S01_U__Wifi_NOT_AVAILABLE,
          wifiAlt: 'tooltip.wifiNotAvailableSegment',
        };
      // 2.2) Wi-Fi利用区分="1"(あり)の場合、Wi-Fi有を表すイメージ
      case '1':
        return {
          wifiIcon: this.appConstants.IMG.S01_P030_S01_U__Wifi_AVAILABLE,
          wifiAlt: 'alt.wifiAvailable',
        };
      // 2.3) Wi-Fi利用区分="2"(可能性あり)の場合、Wi-Fi有の可能性ありを表すイメージ
      case '2':
        return {
          wifiIcon: this.appConstants.IMG.S01_P030_S01_U__Wifi_POSSIBLY_AVAILABLE,
          wifiAlt: 'alt.wifiSomeSegmentsAvailable',
        };
      default:
        return {
          wifiIcon: '',
          wifiAlt: '',
        };
    }
  }

  buildTooltipContent(serviceType: string, alt: string, flight: Type1): string {
    // 当該セグメント情報.isJapanDomesticFlight=trueの場合、セグメント区分に"D"を設定する。当該セグメント情報.isJapanDomesticFlight=falseの場合、セグメント区分に"I"を設定する。
    let segmentType = flight?.isJapanDomesticFlight === false ? 'I' : 'D';
    // サービスタイプ、かつ当該セグメント情報.cabin、かつセグメント区分を基に「イベントID(1)」で取得したサービスコンテンツリストをフィルタして、サービス説明(コンテンツページへのリンク)を取得する。
    let result =
      this._serviceContentsMaster?.find(
        (item) => item.service_type == serviceType && item.cabin == flight.cabin && item?.segment_type === segmentType
      )?.service_sentence ?? '';

    const label = this._staticMsg.transform(alt);
    return this._metaUrlPipe.transform(result) || label;
  }

  override reload(): void {
    // Add comment to passed SonarLint
  }

  override destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }
}
